import { getFreeSpinAward } from '../core/free-spins';
import { addMoneyCents, assertBetCents, assertMoneyCents } from '../core/money';
import { REEL_STRIPS } from '../core/reel-strips';
import { createGrid, createGridFromStops, type FreeSpinStopProfiles } from '../core/reels';
import { resolveSpin } from '../core/spin-engine';
import type {
  MoneyCents,
  RandomSource,
  ReelStops,
} from '../core/types';
import {
  applyLegacyPotOfGreedOverlay,
  createPotOfGreedEffect,
  selectPotOfGreedPosition,
} from './pot-of-greed';
import {
  applyStickyWilds,
  collectNewStickyWilds,
  mergeStickyWilds,
  normalizeStickyWilds,
} from './sticky-wilds';
import type {
  BonusEvent,
  BonusSpinResult,
  BonusState,
  EgyptianGodEvent,
  PogEvaluationMode,
} from './types';
import {
  applyObelisk,
  applySlifer,
  createExtremeFreeSpinProfiles,
  rollEgyptianGods,
} from './egyptian-gods';

export interface PlayBonusSpinOptions {
  readonly overrideStops?: ReelStops;
  readonly freeSpinStopProfiles?: FreeSpinStopProfiles;
  readonly pogEvaluationMode?: PogEvaluationMode;
  readonly pogChance?: number;
  readonly enableEgyptianGods?: boolean;
}

function assertCount(value: number, label: string): void {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError(`${label} muss eine nichtnegative sichere Ganzzahl sein.`);
  }
}

export function assertBonusState(state: BonusState): void {
  assertCount(state.remainingSpins, 'Verbleibende Freispiele');
  assertCount(state.totalAwardedSpins, 'Vergebene Freispiele');
  assertCount(state.spinsPlayed, 'Gespielte Freispiele');
  assertCount(state.retriggerCount, 'Retrigger-Anzahl');
  assertCount(state.raBoostCount ?? 0, 'Ra-Stufe');
  assertCount(state.obeliskActivationCount ?? 0, 'Obelisk-Stufe');
  assertMoneyCents(state.totalWinCents, 'Feature-Gesamtgewinn');
  normalizeStickyWilds(state.stickyWilds);
  if (state.initialScatterCount < 3 || state.initialScatterCount > 5) {
    throw new RangeError('Initiale Scatter-Anzahl muss 3, 4 oder 5 sein.');
  }
  if (state.remainingSpins + state.spinsPlayed !== state.totalAwardedSpins) {
    throw new Error('Freispielzähler sind nicht konsistent.');
  }
  if (state.active !== (state.remainingSpins > 0)) {
    throw new Error('Aktivstatus und verbleibende Freispiele widersprechen sich.');
  }
}

function freezeBonusState(state: BonusState): BonusState {
  const frozen = Object.freeze({
    ...state,
    stickyWilds: normalizeStickyWilds(state.stickyWilds),
  });
  assertBonusState(frozen);
  return frozen;
}

export function startBonus(scatterCount: number): BonusState | null {
  const awardedSpins = getFreeSpinAward(scatterCount);
  if (awardedSpins === 0) return null;
  const initialScatterCount = Math.min(scatterCount, 5) as 3 | 4 | 5;
  return freezeBonusState({
    active: true,
    started: false,
    initialScatterCount,
    remainingSpins: awardedSpins,
    totalAwardedSpins: awardedSpins,
    spinsPlayed: 0,
    totalWinCents: 0,
    stickyWilds: Object.freeze([]),
    pogTriggered: false,
    retriggerCount: 0,
    raBoostCount: 0,
    obeliskActivationCount: 0,
  });
}

export function beginBonus(state: BonusState): BonusState {
  assertBonusState(state);
  if (!state.active) throw new Error('Ein beendetes Feature kann nicht gestartet werden.');
  if (state.started) throw new Error('Das Feature wurde bereits gestartet.');
  return freezeBonusState({ ...state, started: true });
}

export function playBonusSpin(
  state: BonusState,
  betCents: MoneyCents,
  random: RandomSource,
  options: PlayBonusSpinOptions = {},
): BonusSpinResult {
  assertBonusState(state);
  assertBetCents(betCents);
  if (!state.active || state.remainingSpins <= 0) {
    throw new Error('Ein beendetes Feature kann nicht fortgesetzt werden.');
  }
  if (!state.started) throw new Error('Das Feature wurde noch nicht gestartet.');

  const activeProfiles = options.enableEgyptianGods
    ? createExtremeFreeSpinProfiles(state.raBoostCount ?? 0)
    : options.freeSpinStopProfiles;
  const generated = options.overrideStops === undefined
    ? createGrid('free', random, activeProfiles)
    : Object.freeze({
      stops: options.overrideStops,
      grid: createGridFromStops(REEL_STRIPS, options.overrideStops),
    });
  const stickyBefore = normalizeStickyWilds(state.stickyWilds);
  const gridWithExistingSticky = applyStickyWilds(generated.grid, stickyBefore);
  // Reserve POG before the god phase. Gods can neither target nor overwrite it.
  const pogPosition = selectPotOfGreedPosition(
    gridWithExistingSticky,
    stickyBefore,
    state.pogTriggered,
    random,
    options.pogChance,
  );
  const pogEffect = pogPosition === null ? null : createPotOfGreedEffect(pogPosition);
  const pogEvaluationMode = options.pogEvaluationMode ?? 'post-evaluation';
  // A naturally landed WILD is already visible when the gods activate. Treat
  // it as sticky for the current god phase, while keeping its dedicated
  // capture event and presentation later in the timeline.
  const naturalGrid = applyStickyWilds(generated.grid, stickyBefore);
  const newStickyWilds = collectNewStickyWilds(naturalGrid, stickyBefore);
  const triggers = options.enableEgyptianGods ? rollEgyptianGods(random) : null;
  const godEvents: EgyptianGodEvent[] = [];
  let godSticky = mergeStickyWilds(stickyBefore, newStickyWilds);
  let raAward = 0;
  let raBoostCount = state.raBoostCount ?? 0;
  let obeliskActivationCount = state.obeliskActivationCount ?? 0;
  if (triggers?.ra) {
    const fullGrid = godSticky.length === 15;
    const before = raBoostCount;
    raAward = fullGrid ? 3 : 2;
    if (!fullGrid) raBoostCount += 1;
    godEvents.push(Object.freeze({
      god: 'RA', order: 1, raAwardedSpins: raAward as 2 | 3,
      raBoostBefore: before, raBoostAfter: raBoostCount, raFullGrid: fullGrid,
    }));
  }
  if (triggers?.slifer) {
    const slifer = applySlifer(generated.grid, godSticky, pogPosition, random);
    godSticky = slifer.sticky;
    godEvents.push(slifer.event);
  }
  const evaluationGrid = pogEffect !== null && pogEvaluationMode === 'legacy-overlay'
    ? applyLegacyPotOfGreedOverlay(applyStickyWilds(generated.grid, godSticky), pogEffect.position)
    : applyStickyWilds(generated.grid, godSticky);
  let spinResult = resolveSpin({
    mode: 'free', betCents, grid: generated.grid, stops: generated.stops,
    evaluationGrid, wildMultipliers: godSticky,
  });
  if (triggers?.obelisk) {
    obeliskActivationCount += 1;
    const obelisk = applyObelisk(
      generated.grid, generated.stops, betCents, godSticky, pogPosition, obeliskActivationCount,
    );
    spinResult = obelisk.spin;
    godEvents.push(obelisk.event);
  }

  // Preserve naturally landed WILDs. Slifer upgrades are already included in
  // godSticky; Obelisk remains temporary and never enters this state.
  const stickyAfterNaturalWilds = mergeStickyWilds(godSticky, newStickyWilds);

  const retriggerAwarded = getFreeSpinAward(spinResult.scatterCount);
  const pogAward = pogEffect?.awardedFreeSpins ?? 0;
  const stickyAfter = pogEffect === null
    ? stickyAfterNaturalWilds
    : mergeStickyWilds(stickyAfterNaturalWilds, [pogEffect.position]);
  const remainingSpins = state.remainingSpins - 1 + retriggerAwarded + pogAward + raAward;
  const events: BonusEvent[] = [
    Object.freeze({ type: 'line-wins-evaluated', totalWinCents: spinResult.totalWinCents }),
  ];
  if (newStickyWilds.length > 0) {
    events.push(Object.freeze({ type: 'sticky-wilds-captured', positions: newStickyWilds }));
  }
  if (retriggerAwarded !== 0) {
    events.push(Object.freeze({
      type: 'retrigger-awarded',
      scatterCount: spinResult.scatterCount,
      spins: retriggerAwarded,
    }));
  }
  if (pogEffect !== null) events.push(Object.freeze({ type: 'pot-of-greed-awarded', effect: pogEffect }));
  events.push(Object.freeze({ type: 'bonus-spin-complete', remainingSpins }));

  const stateAfter = freezeBonusState({
    ...state,
    active: remainingSpins > 0,
    remainingSpins,
    totalAwardedSpins: state.totalAwardedSpins + retriggerAwarded + pogAward + raAward,
    spinsPlayed: state.spinsPlayed + 1,
    totalWinCents: addMoneyCents(state.totalWinCents, spinResult.totalWinCents),
    stickyWilds: stickyAfter,
    pogTriggered: state.pogTriggered || pogEffect !== null,
    retriggerCount: state.retriggerCount + (retriggerAwarded > 0 ? 1 : 0),
    raBoostCount,
    obeliskActivationCount,
  });

  return Object.freeze({
    spin: spinResult,
    stateBefore: state,
    stateAfter,
    stickyBefore,
    newStickyWilds,
    stickyAfter,
    retriggerAwarded,
    pogEffect,
    pogEvaluationMode,
    godEvents: Object.freeze(godEvents),
    events: Object.freeze(events),
  });
}
